/**
 * HolidayPlanningPanel — 10/02/2026 (Lot M).
 *
 * Affiché en tête de l'onglet « Équipe » dans la page RH. Liste les
 * chauffeurs à envoyer en congés en priorité (calcul via
 * `buildHolidayPlan`). Si Nadia Cheikh est recrutée, affiche aussi son
 * conseil contextualisé en tête.
 *
 * Le composant s'auto-cache si :
 *   • aucun chauffeur n'est éligible (équipe trop fraîche, soldes faibles)
 *   • le joueur est en pression congés < seuil mid-cap (pas urgent)
 *
 * Chaque ligne propose un bouton « Envoyer en congés » qui appelle
 * `sendDriverOnHoliday(driverId)` du gameStore.
 */
import React, { useMemo, useState } from 'react';
import { CalendarDays, Briefcase, ChevronDown, ChevronUp, Send, Info, Sparkles, AlertTriangle, X } from 'lucide-react';
import useGameStore from '../../store/gameStore';
import { buildHolidayPlan, RISK_TONE_LABELS } from '../../store/helpers/holidayPlanning';

const RISK_BG = {
  high:   'bg-rose-500/10 border-rose-400/40',
  medium: 'bg-amber-500/10 border-amber-400/40',
  low:    'bg-emerald-500/10 border-emerald-400/40',
};
const RISK_DOT = {
  high:   'bg-rose-400',
  medium: 'bg-amber-400',
  low:    'bg-emerald-400',
};
const RISK_TEXT = {
  high:   'text-rose-200',
  medium: 'text-amber-100',
  low:    'text-emerald-200',
};

export default function HolidayPlanningPanel() {
  const drivers = useGameStore(s => s.drivers) || [];
  const vehicles = useGameStore(s => s.vehicles) || [];
  const totalDays = useGameStore(s => s.totalDays || 0);
  const hiredSupport = useGameStore(s => s.hiredSupport) || {};
  const sendDriverOnHoliday = useGameStore(s => s.sendDriverOnHoliday);
  const toggleVehicleInterim = useGameStore(s => s.toggleVehicleInterim);
  const [open, setOpen] = useState(false);
  const [feedback, setFeedback] = useState(null);
  // 10/02/2026 (Lot N suite) — Retour user prod : « si la personne est
  // en mission, et qu'on ne veut pas se fâcher avec le client, on ne
  // peut pas déclencher les autres. Il faudrait une alerte quand on
  // clique sur envoyer ». État de la modale de confirmation.
  const [confirmModal, setConfirmModal] = useState(null);

  const nadiaHired = !!hiredSupport['role-nadia-cheikh'];

  const plan = useMemo(() => buildHolidayPlan(drivers, totalDays), [drivers, totalDays]);

  if (plan.length === 0) return null;

  const highCount = plan.filter(p => p.riskTone === 'high').length;
  const mediumCount = plan.filter(p => p.riskTone === 'medium').length;

  /** Internal helper that actually triggers the holiday. */
  const performSend = (driverId, withInterim) => {
    setFeedback(null);
    setConfirmModal(null);
    // 10/02/2026 (Lot N suite) — Si le user dit OUI à l'intérim, on
    // active le toggle AVANT de partir en congés pour que sendDriverOnHoliday
    // ne tente pas de l'auto-activer une 2e fois (idempotent).
    const driver = drivers.find(d => d.id === driverId);
    if (withInterim && driver?.assignedVehicleId && typeof toggleVehicleInterim === 'function') {
      const v = vehicles.find(vv => vv.id === driver.assignedVehicleId);
      if (v && !v.useInterim) toggleVehicleInterim(v.id);
    }
    const res = sendDriverOnHoliday(driverId);
    if (res && res.ok) {
      setFeedback({
        type: 'success',
        message: withInterim
          ? `${driver?.name} part en congés (intérim activé pour assurer le contrat).`
          : res.autoInterimVehicleId
            ? `${driver?.name} part en congés (intérim auto-activé sur le véhicule).`
            : `${driver?.name} part en congés.`,
      });
    } else if (res && res.reason) {
      const reasonLabel = {
        already_on_holiday: 'déjà en congés',
        insufficient_balance: 'solde insuffisant',
        newcomer_lock: `nouveau (à débloquer dans ${res.daysUntilEligible} jours)`,
        not_found: 'introuvable',
      }[res.reason] || res.reason;
      setFeedback({ type: 'error', message: `Envoi refusé : ${reasonLabel}.` });
    }
  };

  const handleSend = (driverId) => {
    // 10/02/2026 (Lot N suite) — Modal d'alerte si le chauffeur est en mission.
    const driver = drivers.find(d => d.id === driverId);
    if (!driver) return;
    // Detect if driver is on a vehicle with an active contract.
    const linkedVehicle = driver.assignedVehicleId
      ? vehicles.find(v => v.id === driver.assignedVehicleId)
      : null;
    const onActiveMission = linkedVehicle
      && linkedVehicle.status === 'active'
      && !!linkedVehicle.assignedContractId;
    if (onActiveMission) {
      // Open the confirmation modal — user picks one of the 3 options.
      setConfirmModal({
        driverId,
        driverName: driver.name,
        vehicleLabel: `${linkedVehicle.brand} ${linkedVehicle.model}`,
      });
      return;
    }
    // No active mission → send straight away.
    performSend(driverId, false);
  };

  return (
    <div
      className="surface-card overflow-hidden border-amber-400/40"
      data-testid="holiday-planning-panel"
    >
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full px-3 py-2.5 flex items-center justify-between bg-amber-500/10 hover:bg-amber-500/15 transition-colors"
        data-testid="holiday-planning-toggle"
        aria-expanded={open}
      >
        <div className="flex items-center gap-2.5">
          <div className="p-1.5 rounded-md bg-amber-500/20 border border-amber-400/40">
            <CalendarDays className="w-4 h-4 text-amber-300" />
          </div>
          <div className="text-left">
            <h3 className="text-sm font-bold text-amber-100">Planification des congés</h3>
            <p className="text-[10px] text-amber-200/70 font-mono">
              {plan.length} chauffeur(s) à planifier
              {highCount > 0 ? ` · ${highCount} urgent(s)` : ''}
              {mediumCount > 0 ? ` · ${mediumCount} à prévoir` : ''}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="hidden md:inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider text-amber-300/70">
            {open ? 'Masquer' : 'Voir le plan'}
          </span>
          {open ? <ChevronUp className="w-4 h-4 text-amber-300" /> : <ChevronDown className="w-4 h-4 text-amber-300" />}
        </div>
      </button>
      {open && (
        <div className="p-3 space-y-3 bg-ink-900/40" data-testid="holiday-planning-content">
          {/* Conseil Nadia */}
          {nadiaHired && (
            <div className="rounded-md border border-accent/40 bg-accent/10 p-3 flex items-start gap-2.5" data-testid="holiday-planning-nadia-advice">
              <Briefcase className="w-4 h-4 text-accent flex-shrink-0 mt-0.5" />
              <div className="space-y-1">
                <p className="text-xs font-bold text-accent">Conseil de Nadia Cheikh — Resp. RH</p>
                <p className="text-[11px] text-slate-200 leading-snug">
                  Voici mon plan étalé sur {plan.length * 7} jours pour ne pas vider l&apos;effectif d&apos;un coup.
                  Confirmez les départs urgents (🔴) en priorité — au-delà du 31 décembre, les chauffeurs en surcharge pression risquent un départ négocié.
                </p>
              </div>
            </div>
          )}
          {!nadiaHired && (
            <div className="rounded-md border border-slate-500/30 bg-slate-700/20 p-3 flex items-start gap-2.5" data-testid="holiday-planning-nadia-hint">
              <Info className="w-4 h-4 text-slate-400 flex-shrink-0 mt-0.5" />
              <p className="text-[11px] text-slate-400 leading-snug">
                Recrutez <strong className="text-accent">Nadia Cheikh (Responsable RH)</strong> pour recevoir un plan personnalisé par messagerie et des alertes proactives sur les chauffeurs en risque de pression congés.
              </p>
            </div>
          )}

          {/* Feedback action */}
          {feedback && (
            <div
              className={`rounded-md px-2.5 py-1.5 text-[11px] ${
                feedback.type === 'success'
                  ? 'bg-emerald-500/10 border border-emerald-400/40 text-emerald-200'
                  : 'bg-rose-500/10 border border-rose-400/40 text-rose-200'
              }`}
              data-testid="holiday-planning-feedback"
            >
              {feedback.message}
            </div>
          )}

          {/* Plan list */}
          <ul className="space-y-1.5" data-testid="holiday-planning-list">
            {plan.map((p, idx) => {
              const tone = RISK_TONE_LABELS[p.riskTone];
              return (
                <li
                  key={p.driverId}
                  className={`rounded-md border px-2.5 py-2 flex items-center gap-3 ${RISK_BG[p.riskTone]}`}
                  data-testid={`holiday-planning-row-${p.driverId}`}
                >
                  <span className={`w-2 h-2 rounded-full ${RISK_DOT[p.riskTone]} flex-shrink-0 mt-0.5`} />
                  <div className="flex-1 min-w-0">
                    <p className={`text-xs font-bold leading-tight ${RISK_TEXT[p.riskTone]}`}>
                      {p.driverName}
                    </p>
                    <p className="text-[10px] text-slate-400 font-mono">
                      Solde {p.balance} j
                      {p.pressureLoss > 0 ? ` · -${p.pressureLoss} satisfaction (pression)` : ''}
                      <span className="hidden md:inline"> · suggéré : {idx === 0 ? 'cette semaine' : `J+${idx * 7}`}</span>
                    </p>
                  </div>
                  <span className={`hidden lg:inline-flex text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ${RISK_TEXT[p.riskTone]} bg-ink-900/40`}>
                    {tone.label}
                  </span>
                  <button
                    type="button"
                    onClick={() => handleSend(p.driverId)}
                    className="text-[11px] font-bold uppercase tracking-wider px-2.5 py-1.5 rounded transition-colors inline-flex items-center gap-1 bg-amber-400 hover:bg-amber-300 text-slate-900"
                    data-testid={`holiday-planning-send-${p.driverId}`}
                    title={idx === 0 ? 'Envoyer immédiatement en congés' : `Conseillé J+${idx * 7}, mais vous pouvez envoyer maintenant`}
                  >
                    <Send className="w-3 h-3" /> Envoyer
                  </button>
                </li>
              );
            })}
          </ul>

          {/* Footer */}
          <p className="text-[10px] text-slate-500 font-mono flex items-center gap-1">
            <Sparkles className="w-3 h-3 text-amber-300" />
            Intérim activé automatiquement sur les véhicules en mission pendant les absences.
          </p>
        </div>
      )}
      {/* 10/02/2026 (Lot N suite) — Modale de confirmation pour
          chauffeur en mission. 3 choix explicites. */}
      {confirmModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
          role="dialog"
          aria-modal="true"
          data-testid="holiday-planning-confirm-modal"
          onClick={() => setConfirmModal(null)}
        >
          <div
            className="relative bg-gradient-to-br from-slate-900 via-slate-850 to-slate-900 border border-amber-400/40 rounded-xl shadow-2xl w-full max-w-md p-4"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => setConfirmModal(null)}
              className="absolute top-2 right-2 p-1.5 rounded-full bg-ink-700/80 hover:bg-ink-600 text-slate-300"
              aria-label="Fermer"
              data-testid="holiday-planning-confirm-close"
            >
              <X className="w-3.5 h-3.5" />
            </button>
            <div className="flex items-start gap-2.5 mb-3">
              <div className="p-1.5 rounded-md bg-amber-500/20 border border-amber-400/40">
                <AlertTriangle className="w-4 h-4 text-amber-300" />
              </div>
              <div>
                <h3 className="text-sm font-bold text-amber-100">
                  {confirmModal.driverName} est en mission active
                </h3>
                <p className="text-[11px] text-slate-300 leading-snug mt-1">
                  Véhicule : <span className="font-mono text-amber-200">{confirmModal.vehicleLabel}</span>
                </p>
                <p className="text-[11px] text-slate-400 leading-snug mt-1.5">
                  Si vous l&apos;envoyez en congés maintenant, son contrat client risque d&apos;être interrompu. Souhaitez-vous lui assigner un intérimaire ?
                </p>
              </div>
            </div>
            <div className="space-y-2">
              <button
                type="button"
                onClick={() => performSend(confirmModal.driverId, true)}
                className="w-full text-left rounded-md border border-emerald-400/40 bg-emerald-500/10 hover:bg-emerald-500/20 px-3 py-2 transition-colors flex items-start gap-2"
                data-testid="holiday-planning-confirm-yes"
              >
                <span className="text-base">✓</span>
                <div>
                  <p className="text-xs font-bold text-emerald-200">Oui, activer un intérimaire</p>
                  <p className="text-[10px] text-slate-400 leading-tight mt-0.5">Le contrat continue, coût intérim quotidien facturé pendant l&apos;absence.</p>
                </div>
              </button>
              <button
                type="button"
                onClick={() => performSend(confirmModal.driverId, false)}
                className="w-full text-left rounded-md border border-amber-400/40 bg-amber-500/10 hover:bg-amber-500/20 px-3 py-2 transition-colors flex items-start gap-2"
                data-testid="holiday-planning-confirm-no"
              >
                <span className="text-base">⚠</span>
                <div>
                  <p className="text-xs font-bold text-amber-200">Non, partir sans intérim</p>
                  <p className="text-[10px] text-slate-400 leading-tight mt-0.5">Le contrat sera dégradé (jusqu&apos;à -10 % CA). Tolérance ≤ 5 jours sur contrats longs.</p>
                </div>
              </button>
              <button
                type="button"
                onClick={() => setConfirmModal(null)}
                className="w-full text-left rounded-md border border-slate-500/40 bg-slate-700/30 hover:bg-slate-700/50 px-3 py-2 transition-colors flex items-start gap-2"
                data-testid="holiday-planning-confirm-cancel"
              >
                <span className="text-base">✗</span>
                <div>
                  <p className="text-xs font-bold text-slate-200">Ne pas envoyer en congés</p>
                  <p className="text-[10px] text-slate-400 leading-tight mt-0.5">Annule l&apos;envoi. Le chauffeur reste sur son contrat.</p>
                </div>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
